import { Box, DialogActions, Grid, styled } from '@mui/material';
import MediaField from 'Components/Forms/MediaField';
import TextFieldLimited from 'Components/Forms/TextFieldLimited';
import { mergeDefaults } from 'utils/form.utils';
import componentCreationSchema from 'FeatureModules/ComponentChain/schemas/components.schemas';
import { FieldWrapper, ThemeButton } from 'DesignSystem';
import { FastField as Field, Form, Formik, FormikProps } from 'formik';
import { TextField } from 'formik-mui';
import isEqual from 'lodash/isEqual';
import React, { FC, RefObject } from 'react';
import { IComponentItem } from 'FeatureModules/ComponentChain/types/component.types';
import { ICropping } from 'FeatureModules/Media/types/media.types';
import PartnerAutocomplete from '../../../Activity/Components/Forms/PartnerAutocompleteWrapper';
import RawMaterialSelection from './RawMaterialSelection';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';

const StyledCard = styled(Grid)(({ theme }) => ({
  background: theme.custom.themeColors.white,
  boxShadow: theme.custom.shadows[4],
  borderRadius: 6,
  minHeight: 150,
  width: '100%',
  padding: theme.spacing(2),
}));

const StyledTextFieldLimited = styled(TextFieldLimited)(() => ({
  '&.MuiTextField-root': {
    '& .MuiInputBase-input': {
      fontSize: 12,
      height: 16,
      padding: '10px 12px 8px',
    },
  },
}));

const StyledTextField = styled(TextField)(() => ({
  '&.MuiTextField-root': {
    '& .MuiFormLabel-root': {
      fontSize: 11,
      fontWeight: 300,
      '&[data-shrink="false"]': {
        transform: 'translate(14px, 10px) scale(1)',
      },
    },
    '& .MuiInputBase-input': {
      height: 16,
      padding: '9px 12px',
    },
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(4, 0, 0, 0),
  justifyContent: 'space-between',
}));

export type TFormValues = {
  title: string;
  image: {
    imageId: string;
    crop?: ICropping;
  };
  ownedBy?: { id: string };
  rawMaterialIds: string[];
  articleNumber?: string;
};

interface ICreateEditComponentFormProps {
  formRef: RefObject<FormikProps<TFormValues>>;
  component?: Omit<IComponentItem, 'inUse' | 'modifiedTimestamp'>;
  /*
   *Pass this if the company select should already be prefilled
   *  Warning: This overrides the partner on the componentItem and disabled the field (so the partner can not be changed)
   */
  company?: IBasePartnerCompany;

  /**
   * The rest of selectable raw material ids for filtering the component selection
   */
  selectableRawMaterialIds?: string[];

  hidePartner?: boolean;
  language?: string;
  onSubmit: (values: TFormValues) => void;
  onCancel?: () => void;
}

const CreateEditComponentForm: FC<ICreateEditComponentFormProps> = ({
  formRef,
  component,
  company,
  hidePartner,
  selectableRawMaterialIds,
  language,
  onSubmit,
  onCancel,
}) => {
  const handleSubmit = (values: TFormValues) => {
    onSubmit(values);
  };

  const initialValues = mergeDefaults(componentCreationSchema.default(), {
    title: component?.title,
    image: component?.image,
    ownedBy: company || component?.ownedBy,
    rawMaterialIds: selectableRawMaterialIds
      ? selectableRawMaterialIds
      : component?.rawMaterials?.map(({ id }) => id),
    articleNumber: component?.articleNumber,
  });

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={componentCreationSchema}
      enableReinitialize
      initialStatus={{
        lang: language,
      }}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, isValid, values }) => (
        <Form data-testid="create-product-form">
          <StyledCard container>
            <Grid item xs={3}>
              <FieldWrapper>
                <Field
                  component={MediaField}
                  name="image"
                  data-testid="field-component-image"
                  legacyImage={false}
                  styles={{
                    width: 116,
                    height: 116,
                    maxWidth: '100%',
                  }}
                />
              </FieldWrapper>
            </Grid>
            <Grid container xs={9} alignContent="center">
              <Grid item xs={12}>
                <Box display="flex" mb={2.5}>
                  <FieldWrapper width="calc(100% - 126px)" mr={2}>
                    <Field
                      name="title"
                      fullWidth
                      placeholder="E.g. Coffee beans, Cocoa butter"
                      component={StyledTextFieldLimited}
                      variant="outlined"
                      charLimit={50}
                      label="Title"
                      inputProps={{
                        'data-testid': 'field-component-title',
                      }}
                    />
                  </FieldWrapper>
                  <FieldWrapper width={110}>
                    <Field
                      component={StyledTextField}
                      placeholder="1234567"
                      fullWidth
                      name="articleNumber"
                      variant="outlined"
                      label="Article number"
                      data-testid="article-number-input"
                    />
                  </FieldWrapper>
                </Box>
              </Grid>
              <Grid container spacing={2}>
                {!hidePartner && (
                  <Grid item xs={6}>
                    <PartnerAutocomplete
                      name="ownedBy"
                      disabled={!!company}
                      helperText="Select the actor that produces this component. This could be a partner or your own company. Assigning an invited partner as owner means that they also have access to editing this component."
                    />
                  </Grid>
                )}
                <Grid item xs={hidePartner ? 12 : 6}>
                  <RawMaterialSelection
                    name="rawMaterialIds"
                    selectableRawMaterialIds={selectableRawMaterialIds}
                    // Disable creation of new raw materials when selectableRawMaterialIds is provided, as this likely means we are in a context where only a specific set of raw materials should be used (e.g. when creating a component from the product page with preselected raw materials based on the selected site)
                    enableAddNewItem={!selectableRawMaterialIds}
                    size="small"
                    helperText="Select the raw material (Unprocessed origin material) content of this component (E.g. ‘Tomato’ for ‘Dried tomato flakes’). This will help trace and filter your ingredients per raw material."
                    data-testid="field-component-raw-materials"
                  />
                </Grid>
              </Grid>
            </Grid>
          </StyledCard>
          <StyledDialogActions>
            <ThemeButton
              color="BLUE_ICE"
              size="large"
              className="button-wide"
              data-testid="cancel-component-button"
              onClick={onCancel}
            >
              Cancel
            </ThemeButton>
            <ThemeButton
              loading={isSubmitting}
              disabled={!isValid || (!!component && isEqual(initialValues, values))}
              color="PRIMARY"
              size="large"
              className="button-wide"
              type="submit"
              data-testid="create-component-button"
            >
              Save
            </ThemeButton>
          </StyledDialogActions>
        </Form>
      )}
    </Formik>
  );
};

export default CreateEditComponentForm;
